import { KEY_CODES } from 'lib/constants';
import { pui } from 'lib/perspectives-ui';
import presentationUI from 'lib/presentation-ui';
import { COLOR_VAR_TO_HEX_MAPPING, LINK_COLORS, LINK_COLORS_MAPPING, LINK_WIDTHS_MAPPING, DEFAULTS, DEFAULT_SIBLING_LINK_ANGLE } from 'lib/constants.js';
import { usui } from 'lib/upsell-ui';
import { getPointFromAngles } from 'lib/geom';

class RelationshipUI {
  constructor() {
    this.canvas = {};
  }

  setCanvas(canvas) {
    this.canvas = canvas;
  }

  straightenLine(link, operationMessages) {
    const prevAngleStartToR = link.angleStartToR;
    const prevAngleEndToR = link.angleEndToR;
    link.setAngles({
      angleStartToR: Math.PI / 1000,
      angleEndToR: Math.PI / -1000,
    });
    const linkTag = this.canvas.linkTags[link.id];
    if (linkTag) {
      linkTag.updatePosition();
    }
    if (operationMessages) {
      const message = this.canvas.operation.messages.setLinkAngles({
        linkUUID: link.id,
        angleStartToR: link.angleStartToR,
        angleEndToR: link.angleEndToR,
        prevAngleStartToR,
        prevAngleEndToR,
      });
      operationMessages.push(message);
    } else {
      this.canvas.operation.setLinkAngles({
        linkUUID: link.id,
        angleStartToR: link.angleStartToR,
        angleEndToR: link.angleEndToR,
        prevAngleStartToR,
        prevAngleEndToR,
        onFailure: (err) => {
          console.log(`setLinkAngles failure: ${err}`);
        }
      });
    }
  }

  createLinksToNodes(fromNode,toNodes) {
    if (!fromNode || !toNodes.length) {
      return;
    }

    const messages = [];
    const linkIds = [];
    const lastCurvature = localStorage.getItem(`${window.map_id}-last-line-curvature`) || 'arc';
    for (const node of toNodes) {
      const link = {
        fromNodeId: fromNode.id,
        toNodeId: node.id,
        lineCurvature: lastCurvature,
        direction: 'right',
        createdById: window.user_id,
      };
      link.id = this.canvas.store.addLink(link);
      linkIds.push(link.id);

      pui.addLinkToActivePerspectives(link, messages);

      messages.push(
        this.canvas.operation.messages.addLink({
          link: link,
        })
      );
    }

    this.canvas.linksTag.update();
    linkIds.map(id => this.canvas.linkTags[id].update());

    if (linkIds.length) {
      self.canvas.setSelectedElements(linkIds.map(id => this.canvas.linkTags[id]).filter(l => l));
    }

    this.canvas.operation.batchOperation({
      messages,
      onFailure: err => console.log(`batch operation failure: ${err}`),
    });
  }


  startCreationFromClick() {
    const selectedNodes = this.canvas.getSelectedNodes();
    if (selectedNodes.length == 1) {
      return this.startCreation(selectedNodes[0]);
    } else {
      this.canvas.floatingRLineTag.summon({});
    }
  }


  startCreation(node) {
    const fromNode = node;

    presentationUI.pause();
    document.body.classList.add('link-dragging');
    this.canvas.setLinkDragging(fromNode.id);

    const rect = this.canvas.nodeTags[fromNode.id].refs.container.getBoundingClientRect();
    const {scale, tx, ty} = this.canvas.getMatrix();
    const left = (rect.left - tx) / scale;
    const top = (rect.top - ty) / scale;

    const bottom = (rect.bottom - ty) / scale;
    const right = (rect.right - tx) / scale;

    const width = (rect.right - rect.left) / scale;
    const height = (rect.bottom - rect.top) / scale;
    const fromNodeRect = { left, top, bottom, right, width, height };
    let linkId;
    const sourceNodeTag = this.canvas.nodeTags[fromNode.id];
    if (!(sourceNodeTag && sourceNodeTag.refs && sourceNodeTag.refs.container)) return;
    sourceNodeTag.refs.container.classList.add('link-dragging-no-highlight');
    this.canvas.nodeTools.hide();

    this.canvas.setDragLink({
      fromNodeId: fromNode.id,
      fromNodeRect,
      x: (this.canvas.mouseX - tx) / scale,
      y: (this.canvas.mouseY - ty) / scale,
    });
    this.canvas.linksTag.update();

    const moveHandler = e => {
      this.canvas.setDragLink({
        fromNodeId: fromNode.id,
        fromNodeRect,
        x: (e.clientX - tx) / scale,
        y: (e.clientY - ty) / scale,
      });
      this.canvas.linksTag.update();
    };
    document.body.addEventListener('mousemove', moveHandler);

    const keyHandler = e => {
      e.stopPropagation();
      const { ESCAPE, DELETE, BACKSPACE } = KEY_CODES;
      if ([ESCAPE, DELETE, BACKSPACE].includes(e.keyCode)) {
        document.body.removeEventListener('click', clickHandler );
        document.body.removeEventListener('mousemove', moveHandler);
        document.body.removeEventListener('keyup', keyHandler);
        document.body.classList.remove('link-dragging');
        sourceNodeTag.refs.container.classList.remove('link-dragging-no-highlight');
        this.canvas.setLinkDragging(false);
        this.canvas.linksTag.update();
      }
    };

    const clickHandler = e => {
      document.body.removeEventListener('click', clickHandler );
      document.body.removeEventListener('mousemove', moveHandler);
      document.body.removeEventListener('keyup', keyHandler);
      sourceNodeTag.refs.container.classList.remove('link-dragging-no-highlight');

      const targetNodeTag = this.canvas.mouseEnteredNode;
      if (targetNodeTag) {
        const targetNode = targetNodeTag.instance;
        this.canvas.linksTag.update();

        const lastNodeId = this.canvas.linkDragging;
        const lastNode = this.canvas.store.getNode(lastNodeId);

        this.canvas.nodeTags[targetNode.id].discoverPosition();
        this.canvas.nodeTags[lastNodeId].discoverPosition();

        let angleStartToR, angleEndToR = null;

        if (targetNode.getTopLevelParent() === lastNode.getTopLevelParent() && targetNode.getGoverningLayoutName() !== 'layout-freehand' && lastNode.getGoverningLayoutName() !== 'layout-freehand') {
          if (targetNode.isAboveInLayout(lastNode)) {
            angleEndToR = DEFAULT_SIBLING_LINK_ANGLE;
          } else {
            angleEndToR = DEFAULT_SIBLING_LINK_ANGLE * -1;
          }
        }

        angleStartToR = angleEndToR * -1;


        if (lastNode !== targetNode) {

          const lastCurvature = localStorage.getItem(`${window.map_id}-last-line-curvature`) || 'arc';
          const link = {
            fromNodeId: lastNode.id,
            toNodeId: targetNode.id,
            direction: 'right',
            lineCurvature: lastCurvature,
            angleStartToR,
            angleEndToR,
          };

          link.id = this.canvas.store.addLink(link);
          linkId = link.id;
          const messages = [
            this.canvas.operation.messages.addLink({
              link: link,
            })
          ];

          pui.addLinkToActivePerspectives(link, messages);

          this.canvas.operation.batchOperation({
            messages,
            onFailure: err => console.log(`batch operation failure: ${err}`),
          });
        }

        this.canvas.nodeTools.summon({
          node: this.canvas.nodeTags[targetNode.id],
          positionReferenceElement: this.canvas.nodeTags[targetNode.id].refs.container,
        });
      }


      document.body.classList.remove('link-dragging');
      this.canvas.setLinkDragging(false);
      this.canvas.update();
      this.canvas.linksTag.update();
      if (linkId) {
        self.canvas.setSelectedElements([this.canvas.linkTags[linkId]]);
      }
    };


    document.body.addEventListener('click', clickHandler);
    document.body.addEventListener('keyup', keyHandler);

    return false;
  }

  setColor(color,linkIds) {
    const messages = [];
    for (const id of linkIds) {
      const link = this.canvas.store.getLink(id);
      if (this.canvas.linkTags[link.id]) {
        const prevColor = link.color;
        const newColor = LINK_COLORS_MAPPING[color] || DEFAULTS.LINK_COLOR;
        link.setColor(newColor);
        this.canvas.linkTags[link.id].updateStrokeColor();
        if (link.linkNode()) {
          this.canvas.nodeTags[link.linkNode().id].update({ forceUpdate: true });
        }
        messages.push( this.canvas.operation.messages.setLinkColor({
          linkUUID: link.id,
          prevColor,
          color: newColor
        }));
      }
    }

    if (messages.length) {
      this.canvas.operation.batchOperation({
        messages,
        onFailure: (err) => {
          console.log(`setLinkColor batch failure: ${err}`);
        }
      });
    }
  }

  setLineType(type,linkIds) {
    const messages = [];
    for (const id of linkIds) {
      const link = this.canvas.store.getLink(id);
      if (this.canvas.linkTags[link.id]) {
        const prevLineType = link.lineType;
        const newType = type;
        link.setLinkType(newType);
        this.canvas.linkTags[link.id].updateStyle();

        messages.push( this.canvas.operation.messages.setLinkType({
          linkUUID: link.id,
          lineType: newType,
          prevLineType,
        }));
      }
    }

    if (messages.length) {
      this.canvas.operation.batchOperation({
        messages,
        onFailure: (err) => {
          console.log(`setLineType batch failure: ${err}`);
        }
      });
    }
  };

  setLineWidth(width,linkIds) {
    const messages = [];
    for (const id of linkIds) {
      const link = this.canvas.store.getLink(id);
      if (this.canvas.linkTags[link.id]) {
        const prevLineWidth = link.lineWidth;
        const newWidth = width;
        link.setLinkWidth(newWidth);
        this.canvas.linkTags[link.id].updateStyle();

        messages.push( this.canvas.operation.messages.setLinkWidth({
          linkUUID: link.id,
          lineWidth: newWidth,
          prevLineWidth,
        }));
      }
    }

    if (messages.length) {
      this.canvas.operation.batchOperation({
        messages,
        onFailure: (err) => {
          console.log(`setLineWidth batch failure: ${err}`);
        }
      });
    }
  }

  getLineCurvature(links = this.canvas.getSelectedLinks()) {
    if (!(links && links.length)) return;
    const { lineCurvature: original } = links[0];

    // If links have different curvatures set, curvature is null
    if (links.some(({ lineCurvature }) => lineCurvature !== original)) {
      return null;
    }

    return original;
  }

  setLineCurvature(lineCurvature, linkIds) {
    const { store, operation, linkTags } = this.canvas;
    localStorage.setItem(`${window.map_id}-last-line-curvature`, lineCurvature);

    const messages = linkIds.filter(id => !!linkTags[id]).map(linkUUID => {
      const link = store.getLink(linkUUID);
      const { lineCurvature: prevLineCurvature } = link;

      link.setLinkCurvature(lineCurvature);
      linkTags[linkUUID].updateCurvature();

      return operation.messages.setLinkCurvature({ linkUUID, lineCurvature, prevLineCurvature });
    });

    if (messages.length) {
      operation.batchOperation({
        messages,
        onFailure: err => console.error(`setLineCurvature batch failure: ${err}`),
      });
    }
  }

  setLineDirection(direction,linkIds) {
    const messages = [];
    for (const id of linkIds) {
      const link = this.canvas.store.getLink(id);
      if (this.canvas.linkTags[link.id]) {
        const prevDirection = link.direction;
        link.setDirection(direction);
        this.canvas.linkTags[link.id].updateDirectionState();

        messages.push( this.canvas.operation.messages.setLinkDirection({
          linkUUID: link.id,
          direction,
          prevDirection
        }));
      }
    }

    if (messages.length) {
      this.canvas.operation.batchOperation({
        messages,
        onFailure: (err) => {
          console.log(`setLineDirection batch failure: ${err}`);
        }
      });
    }
  }


  getBulkLinkColor(linkIds) {
    const colors = [];
    const seen = {};
    for (const id of linkIds) {
      const link = this.canvas.store.getLink(id);
      if (link && link.color && !seen[link.color]) {
        colors.push(link.color);
        seen[link.color] = true;
      }
    }
    if (colors.length == 1) {
      return colors[0];
    }
    return null;
  }

  getBulkLineType(linkIds) {
    const lineTypes = [];
    const seen = {};
    for (const id of linkIds) {
      const link = this.canvas.store.getLink(id);
      if (link && link.lineType && !seen[link.lineType]) {
        lineTypes.push(link.lineType);
        seen[link.lineType] = true;
      }
    }
    if (lineTypes.length == 1) {
      return lineTypes[0];
    }
    return null;
  }

  getBulkLineWidth(linkIds) {
    const lineWidths = [];
    const seen = {};
    for (const id of linkIds) {
      const link = this.canvas.store.getLink(id);
      if (link && link.lineWidth && !seen[link.lineWidth]) {
        lineWidths.push(link.lineWidth);
        seen[link.lineWidth] = true;
      }
    }
    if (lineWidths.length == 1) {
      return lineWidths[0];
    }
    return null;
  }

  getColorPreviewStyle(linkIds) {
    if (linkIds) {
      const color = this.getBulkLinkColor(linkIds);
      if (color) {
        return COLOR_VAR_TO_HEX_MAPPING[color];
      }
    }
    return '#acb9c8';
  }

  getLinePreviewStyle(linkIds) {
    let lineType = this.getBulkLineType(linkIds);
    if (!lineType) {
      lineType = 'solid';
    }
    const dasharrayValues = {
      'dotted': '4px 4px',
      'dashed': '9px 3px',
      'solid': '',
    };
    return dasharrayValues[lineType] || '';
  }

  getLinePreviewWidth(linkIds) {
    const lineWidth = this.getBulkLineWidth(linkIds);
    const preview_link_widths_mapping = {
      'xs': '2px',
      's': '4px',
      'm': '6px',
      'l': '8px',
      'xl': '10px',
    };
    if (!lineWidth) {
      return preview_link_widths_mapping.s;
    }
    return preview_link_widths_mapping[lineWidth] || preview_link_widths_mapping.s;
  }

  getArcMidPoint(linkId) {
    const link = this.canvas.store.getLink(linkId);
    const { fromPoint, toPoint } = canvas.linkTags[link.id].describeArc();
    return getPointFromAngles(
      fromPoint,
      link.angleStartToR,
      toPoint,
      link.angleEndToR
    );
  }

  addCardsToLinks(linkIds) {
    if (usui.userIsOverLimit()) {
      this.canvas.showPaymentModal();
      return;
    }

    const operationMessages = [];
    for (const id of linkIds) {
      const link = this.canvas.store.getLink(id);
      if (link.linkNode()) {
        continue;
      }

      const {x, y} = this.getArcMidPoint(link.id);

      const { nodeTag, messages } = this.canvas.createNewNode({x, y}, link, false );
      messages.forEach(m => operationMessages.push(m));
      nodeTag.updateLayers({callFocusTitle: true});
    }
    if (operationMessages.length) {
      self.operation.batchOperation({
        messages: operationMessages,
        onFailure: (err) => {
          console.log(`addCardsToLinks batchOperation failure: ${err}`);
        },
      });
    }
  }
}

const rui = new RelationshipUI();

export { RelationshipUI, rui };



// WEBPACK FOOTER //
// ./starling/src/lib/relationship-ui.js